import type { TotpStatusResponse } from '../../interfaces/UserApiService';

/** Default snooze length for the activate-TOTP promo when no grace deadline is available. */
export const ACTIVATE_TOTP_DEFAULT_REMIND_DAYS = 7;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * True when the company's `TOTP_REQUIRED` policy is enforced for this user right now:
 * required by policy, not yet enabled, and the grace period (if any) has elapsed.
 * Used to gate the post-login promo overlay into a non-dismissible mandatory mode.
 */
export function isTotpEnrollmentMandatory(status: TotpStatusResponse): boolean {
  if (!status.requiredByPolicy || status.enabled) {
    return false;
  }
  if (!status.gracePeriodEnds) {
    return true;
  }
  return Date.now() > new Date(status.gracePeriodEnds).getTime();
}

/**
 * Days to snooze the activate-TOTP promo from `gracePeriodEnds`.
 * - Missing/invalid date → 7 days
 * - More than 7 days left → cap at 7
 * - Otherwise → remaining full days until the deadline (ceil, min 1)
 */
export function getActivateTotpRemindDays(gracePeriodEnds?: string | null): number {
  if (gracePeriodEnds == null || gracePeriodEnds === '') {
    return ACTIVATE_TOTP_DEFAULT_REMIND_DAYS;
  }

  const endsAtMs = new Date(gracePeriodEnds).getTime();
  if (Number.isNaN(endsAtMs)) {
    return ACTIVATE_TOTP_DEFAULT_REMIND_DAYS;
  }

  const remainingMs = endsAtMs - Date.now();
  if (remainingMs <= 0) {
    return 1;
  }

  const remainingDays = Math.ceil(remainingMs / MS_PER_DAY);
  return Math.min(ACTIVATE_TOTP_DEFAULT_REMIND_DAYS, remainingDays);
}
